// 🔧 Core libraries
import clsx from "clsx";
import {Fragment, useEffect, useState, createRef, useRef, RefObject, ReactNode} from "react"; // if not already imported
import { observer } from "mobx-react-lite";
import { useDrop } from "react-dnd";

// 🧩 Types
import { 
  IColumnsBlockData,
  IButtonBlockData,
	IDividerBlockData,
  IHeadingBlockData,
  ITextBlockData,
  IImageBlockData,
  IHeroSliderBlockData,
  IVideoBlockData,
  ISocialsBlockData,
  IHtmlBlockData
} from "../../../types/content-block-data";
import {IColumnsBlock, IContentBlock, IDesignBlock} from "../../../types/iblock";
import { 
  ColumnsBlock,
  ButtonBlock,
  DividerBlock,
  HeadingBlock,
  TextBlock,
  ImageBlock,
  HeroSliderBlock,
  VideoBlock,
  SocialsBlock,
  HtmlBlock
} from "./content-blocks";

// 🧠 State
import pageBuilderStore from "../../../stores/pageBuilderStore";
import blocksManager from "../../../stores/blocks-manager";

// 🔁 Utilities
import { createBlockContent } from "../../../utils/blocks-factory";
import { debugPageContent } from "../../../utils/debug";
import serializationManager from "../../../stores/serialization-manager";

// 🎨 UI Components
import DesignBlock from "./design-block";
import DesignDropZone from "./design-drop-zone";
import contentManager from "../../../stores/content-manager";
import glideBarManager from "../../../stores/glidebar-manager";

const CONTENT_BLOCK_TYPES = new Set([
	"BUTTON",
	"DIVIDER",
	"HEADING",
	"TEXT",
	"IMAGE",
	"HERO_SLIDER",
	"VIDEO",
	"SOCIALS",
	"HTML"
]);

/**
 * Props for the `ColumnCell` component.
 *
 * @interface IColumnCellProps
 * @description
 * Defines the expected props passed into a single `ColumnCell`, which represents
 * a mini droppable canvas inside a specific column of a `ColumnsBlock`.
 *
 * @since 2025-06-15
 */
interface IColumnCellProps {

	/** Main ColumnsBlock's contentBlockId */
	colContentBlockId: string;

  /** Index of this column in the parent ColumnsBlock */
  colIndex: number;

  /** Array of content blocks belonging to this specific column */
  children: IDesignBlock[];

	/** The width of the cell itself. */
	widthScale: number;

	/**
   * Optional style object for cell-specific inline CSS.
   * Used for padding, margin, border radius, etc., as set in ColumnsBlock.
   */
  style?: React.CSSProperties;
}

/**
 * `ColumnCell` – A mini drop zone canvas used inside a ColumnsBlock layout.
 *
 * @component
 * @description
 * Behaves like a scaled-down version of `DesignCanvas`, enabling block-level drag-and-drop
 * interactions within a single column. It renders its assigned content blocks and handles
 * block drops into the correct column via the provided `onDropBlock` handler.
 *
 * @features
 * - Accepts sidebar block types for direct drop-in
 * - Displays hover feedback when active
 * - Renders each block using the shared `DesignBlock` shell
 *
 * @since 2025-06-15
 * @lastUpdated 2025-06-16
 */
export default observer(function ColumnCell({ 
	colContentBlockId,
  colIndex, 
  children,
	widthScale,
	style
}: IColumnCellProps) {

  const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);

  /**
   * Tracks the currently editing content block ID (e.g., for inline text editing).
   *
   * @remarks
   * Used to show or hide contextual UIs (like rich text toolbars) tied to editing state.
   * A value of `null` indicates that no block is being edited.
   */
  const [currEditingBlockId, setCurrEditingBlockId] = useState<string | null>(null);

	/**
	 * The DesignBlock that's currently being dragged.
	 */
	const [currentlyDraggingId, setCurrentlyDraggingId] = useState<string | null>(null);

	/**
	 * The DesignBlock that's underneath.
	 */
	const [hoveredTargetId, setHoveredTargetId] = useState<string | null>(null);

	/**
 	 * When a user is trying to drop an unacceptable content block (i.e. ColumnsBlock) onto the ColumnCell's drop area, we will set this flag to true, 
	 * and then render the ColumnCell's style with a red overlay indicator.
	 */
	const [isInvalid, setIsInvalid] = useState(false);

  /**
	 * Handles a new content block being dropped from the sidebar toolbox into this column.
	 *
	 * @function drop ⬇️
	 * @param item - Drag payload containing the block type
	 * @returns An object indicating the drop was handled
	 *
	 * @description
	 * This drop handler is triggered when the user drags a content block
	 * from the sidebar (e.g. text, image, button) and drops it into a ColumnCell.
	 *
	 * It creates a brand new block (via `createBlockContent(...)`),
	 * generates a new `designBlockId`, registers its `ref`,
	 * and appends it to the correct column within the `ColumnsBlock`.
	 *
	 * This drop action is distinct from reordering. It represents *insertion* of a new block.
	 *
	 * @remarks
	 * - This handler does NOT handle reordering existing blocks. See {@link handleDrop} for that.
	 * - The `item.blockType` must match one of the registered ContentBlock types.
	 * - This has been updated to handle disallowed the adding of `ColumnsBlock`.
	 * - When on hover, we also check if the hovering content block is invalid (see above). If invalid, we will display a visual indicator to the user.
	 */
  const [{ isOver }, dropRef] = useDrop({
    accept: [			// Handle drop for any valid sidebar block type (same as DesignCanvas)
			'DESIGN_BLOCKS',
			'CONTENT_BLOCKS',
		],
    drop: (item: { 
			blockType: string;
			contentBlock?: IContentBlock; 
			contentBlockRef?: RefObject<HTMLElement>
			designBlockId?: string; 
			otherColContentBlockId?: string;
			fromColIndex?: number;
			context: string; 
		}) => {
			if (item.contentBlock?.blockType.toLowerCase() ===	"columns")
				return { handled: false };

			// 🧱 Reordering, cross-column or canvas-wide drop
			if (item.blockType === "DESIGN_BLOCKS") {
				if (item.context === "canvas") {
          // Find the block in the main canvas (by designBlockId!)
          const canvasBlocks = contentManager.getDesignContent();
          const srcIndex = canvasBlocks.findIndex(
            (blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === item.designBlockId
          );
          if (srcIndex === -1) return { handled: false };

          // Remove from canvas
          const [movingBlock] = canvasBlocks.splice(srcIndex, 1);
					
					blocksManager.removeDesignTreeEntry((movingBlock as IDesignBlock).contentBlock.contentBlockId);

          // 1. Assign a new wrapper ref and designBlockId for the target DesignBlock
					const newDesignBlockId = crypto.randomUUID();
					const newDesignBlockRef = createRef<HTMLDivElement>();
					
					blocksManager.addDesignTreeEntry({
						contentBlockId: movingBlock.contentBlock.contentBlockId,
						contentBlockType: movingBlock.contentBlock.blockType.toUpperCase(),
						designBlockId: newDesignBlockId,
						designBlockRef: newDesignBlockRef,
						contentBlockRef: item.contentBlockRef ?? createRef<HTMLElement>(),
						location: "columns",
						colIndex,
					});
					
					movingBlock.designBlockId = newDesignBlockId;
					movingBlock.designBlockRef = newDesignBlockRef;

          // Access target column's array
          // TODO: We might need to consider passing our parentColumn's `designBlockId` to each
          // ColumnCell. Again, note that this is not the most precise way to look for a
          // ColumnsBlock.
          const columnsBlock = contentManager.getDesignContent().find(
            (blk: IDesignBlock) => blk.contentBlock.contentBlockId === colContentBlockId
          );
          const colChildren: IDesignBlock[] = (columnsBlock?.contentBlock.blockData as IColumnsBlockData)
            .children[colIndex];

          // STEP 2a & b. If column is empty, push new block
          colChildren.push(movingBlock);
          // Reorder the refs (if necessary). Thinking of this as a form of syncing.
          blocksManager.sortDesignTreeRefs();

					// Let's see what our designTree looks like.
					blocksManager.debugDesignTree();

          // (Optional) Update serialization/UI
          contentManager.setJsonContent(
            serializationManager.fromBlocksToJSON(contentManager.getDesignContent())
          );

					blocksManager.setIsDraggingBlock(false);
					blocksManager.setHoveredDropZoneId(null);
        } else if (item.context === "columns") {
					console.log(`Dropping onto this ColumnsBlock[${colContentBlockId}] from this ColumnsBlocks[${item.otherColContentBlockId}].`);
					handleDrop(false, item.blockType, item.designBlockId!, hoveredTargetId! ?? null, item.fromColIndex!, item.otherColContentBlockId);
				}

				return { handled: true };
			}

			if (blocksManager.getDropHandled()) {
				console.log("🔒 Drop already handled by DropZone. Skipping ColumnCell fallback drop logic.");
				blocksManager.setDropHandled(false);
				return { handled: true };
			}

			// 🎯 For all CONTENT_BLOCK_TYPES (i.e. COLUMNS, BUTTONS, HEADING, TEXT, etc)
	    if (CONTENT_BLOCK_TYPES.has(item.blockType)) {
				console.log("Dropping an", item.blockType, "onto ColumnCell");

		    const newDesignBlockId = crypto.randomUUID();
		    const newContentBlock = createBlockContent(item.blockType);
		    const newDesignBlockRef = createRef<HTMLDivElement>();
		    const contentBlockRef = createRef<HTMLElement>();
		    newContentBlock.contentBlockRef = contentBlockRef;
		    
		    const newDesignBlock: IDesignBlock = {
			    designBlockId: newDesignBlockId,
			    designBlockRef: newDesignBlockRef,
			    contentBlock: newContentBlock,
		    };
		    
		    // Find the ColumnsBlock
		    const columnsBlock = contentManager.getDesignContent().find(
			    (blk: IDesignBlock) => blk.contentBlock.contentBlockId === colContentBlockId
		    );
		    if (!columnsBlock) return;
		    
		    const blockData = columnsBlock.contentBlock.blockData as IColumnsBlockData;
		    const colChildren = blockData.children[colIndex];
		    if (!colChildren) return;
		    
		    // Register in designTree
		    blocksManager.addDesignTreeEntry({
			    contentBlockId: newDesignBlock.contentBlock.contentBlockId,
			    designBlockId: newDesignBlockId,
			    designBlockRef: newDesignBlockRef,
			    contentBlockRef: newContentBlock.contentBlockRef,
			    contentBlockType: item.blockType.toUpperCase(),
			    location: "columns",
			    colIndex: colIndex,
		    });
				
		    colChildren.push(newDesignBlock);

				if (item.blockType.toLowerCase() === "heading" || item.blockType.toLowerCase() === "text") {
          setCurrEditingBlockId(newDesignBlock.contentBlock.contentBlockId);
          blocksManager.setBlockEditing(
            newDesignBlock.contentBlock.contentBlockId,
            item.blockType.toLowerCase()
          );
          pageBuilderStore.setActivePanel("block-properties");
        }
		    
		    // Serialize/Sync
		    contentManager.setJsonContent(
			    serializationManager.fromBlocksToJSON(contentManager.getDesignContent())
		    );
		    blocksManager.sortDesignTreeRefs();
		    blocksManager.setIsDraggingBlock(false);
		    blocksManager.setHoveredDropZoneId(null);
		    
		    return { handled: true };
	    }

			return { handled: false }; // fallback for unexpected types
		},
		hover: (item) => {
	    const isColumnsBlock = item.blockType?.toLowerCase() === "columns" || 
				(item.blockType === "DESIGN_BLOCKS" && item.contentBlock?.blockType?.toLowerCase() === "columns");
	    setIsInvalid(isColumnsBlock);
	  },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

	/**
 	 * Once the user's dragging cursor is no longer over the ColumnCell's drop area, we will check for that and then reset the isInvalid state flag to turn off 
	 * the invalid highlight indicator.
	 */
	useEffect(() => {
	  if (!isOver && isInvalid) {
	    setIsInvalid(false);
	  }
	}, [isOver, isInvalid]);

  // Render different components based on type
	const renderContentBlock = (block: IContentBlock): ReactNode => {
		let ref: RefObject<any>;
		let content: any;
		
		switch (block.blockType) {
			case 'columns': {
				content = block.blockData as IColumnsBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}
				
				// Ensure children is an array of arrays of IDesignBlock
				const children: IDesignBlock[][] = content.children ?? [];

				return (
					<ColumnsBlock
						{...content}
						colContentBlockId={block.contentBlockId}
						contentBlockRef={ref}
						renderBlock={renderContentBlock}
						onClick={(e) => {
							e.stopPropagation(); // Prevent bubbling to canvas
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
						className="w-full"
					/>
				);
			}

			case 'button': {
				content = block.blockData as IButtonBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLButtonElement> ?? createRef<HTMLButtonElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<ButtonBlock
						{...content}
						contentBlockRef={ref}
						onClick={(e) =>{
							e.stopPropagation(); // Prevent bubbling to canvas
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');

							console.log("blockId:", block.contentBlockId);
							console.log("blockType:", block.blockType);
						}}
					/>
				);
			}

			case 'divider': {
				content = block.blockData as IDividerBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as React.RefObject<HTMLHRElement> ?? createRef<HTMLHRElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<div className="relative py-2 px-1 border border-dashed border-gray-300"
						onClick={(e) => {
							e.stopPropagation(); // Prevent bubbling to canvas
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					>
						<DividerBlock 
							{...content} 
							contentBlockRef={ref}
						/>
					</div>
				);
			}

			case 'heading': {
				content = block.blockData as IHeadingBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLHeadingElement> ?? createRef<HTMLHeadingElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
          <HeadingBlock
            {...content}
            contentBlockRef={ref}
            isEditing={currEditingBlockId === block.contentBlockId}
						onFocus={(e) => {
							const el = e.currentTarget;
						  // Check if we're showing the placeholder
						  if (
						    !el.innerText.trim() ||                   // Blank or whitespace
						    el.innerHTML.includes('placeholder')      // Has the placeholder class
						  ) {
						    // Remove placeholder so user can type
						    el.innerHTML = ""; // Now ready for input
						  }
							
							const selection = window.getSelection();
							if (selection && selection.rangeCount > 0) {
								// Save the range (clone, as it can change after blur)
								const caretRange = selection.getRangeAt(0).cloneRange();
								// Update our MobX glideBarState here.
								if (caretRange != null)
									glideBarManager.setCurrCaretPos(caretRange);
							}

							setCurrEditingBlockId(block.contentBlockId);
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType.toLowerCase());
							pageBuilderStore.setActivePanel('block-properties');

							glideBarManager.setActiveBlock(block.contentBlockId, block.blockType.toLowerCase(), ref);
							glideBarManager.setIsEditingHeading(true);
						}}
						onBlur={(e) => {
							let rawHtml = e.currentTarget.innerHTML.trim();
							if (
							  !rawHtml ||
							  rawHtml === "" ||
							  rawHtml === "<br>" ||
							  rawHtml === '<span class="placeholder">Enter a Heading</span>' ||
							  rawHtml === "<span class='placeholder'>Enter a Heading</span>"
							) {
							  content.html = "";
								e.currentTarget.innerHTML = `<span className="placeholder">Enter a heading</span>`;
							} else {
							  content.html = rawHtml;
							}
							
								setTimeout(() => {
									if (!glideBarManager.getGlideBarState().isVisible) {
										setCurrEditingBlockId(null);
										blocksManager.setBlockEditing(undefined, undefined);
										pageBuilderStore.setActivePanel('content');
									}
								}, 1000);
						}}
            onClick={(e) => {
							e.stopPropagation();
						}}
          />
        );
			}

			case 'text': {
				content = block.blockData as ITextBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
          <TextBlock
            {...content}
            contentBlockRef={ref}
            isEditing={currEditingBlockId === block.contentBlockId}
						onFocus={(e) => {
							const el = e.currentTarget;
						  // Treat empty, placeholder, and <p>&nbsp;</p> as "blank"
						  if (
						    !el.innerText.trim() ||  // empty/whitespace only
						    el.innerHTML.includes('placeholder') ||
						    el.innerHTML.trim() === '<p>&nbsp;</p>'
						  ) {
						    el.innerHTML = "<p>&nbsp;</p>"; // Now ready for typing
								// We are only doing this if the TextBlock is still empty with a default value.
								const firstP = el.querySelector('p');
								if (firstP) {
									// Set caret inside the <p> at position 0
									const range = document.createRange();
									range.setStart(firstP, 0);
									range.collapse(true);
									const sel = window.getSelection();
									sel?.removeAllRanges();
									sel?.addRange(range);
								}
						  }
							
							const selection = window.getSelection();
							if (selection && selection.rangeCount > 0) {
								// Save the range (clone, as it can change after blur)
								const caretRange = selection.getRangeAt(0).cloneRange();
								// Update our MobX glideBarState here.
								if (caretRange != null)
									glideBarManager.setCurrCaretPos(caretRange);
							}
							
							setCurrEditingBlockId(block.contentBlockId);
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType.toLowerCase());
							pageBuilderStore.setActivePanel('block-properties');
							// Temporarily shifting it here for testing.
							glideBarManager.setActiveBlock(block.contentBlockId, block.blockType.toLowerCase(), ref);
							glideBarManager.setIsEditingText(true);
						}}
						onBlur={(e) => {
							e.stopPropagation();
							if (e.relatedTarget && ref.current?.contains(e.relatedTarget as Node)) {
								return;
							}

							let rawHtml = e.currentTarget.innerHTML.trim();
							if (
							  !rawHtml ||
							  rawHtml === "" ||
							  rawHtml === "<br>" ||
							  rawHtml === "<p>&nbsp;</p>" ||
							  rawHtml === '<p class="placeholder">Enter your content here.</p>' ||
							  rawHtml === "<p class='placeholder'>Enter your content here.</p>"
							) {
							  content.html = "";
								e.currentTarget.innerHTML = `<p class="placeholder">Enter your content here.</p>`;
							} else {
							  content.html = rawHtml;
							}
							
								setTimeout(() => {
									if (!glideBarManager.getGlideBarState().isVisible) {
										console.log("Losing focus in TextBlock");
										setCurrEditingBlockId(null);
										blocksManager.setBlockEditing(undefined, undefined);
										pageBuilderStore.setActivePanel('content');
									}
								}, 1000);
						}}
          />
        );
			}

			case 'image': {
				content = block.blockData as IImageBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLImageElement> ?? createRef<HTMLImageElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}


				return (
          <ImageBlock
            {...content}
            contentBlockRef={ref}
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
            onImageUrlChange={(newUrl) => {
              content.src = newUrl;
            }}
          />
        );
			}

			case 'hero_slider': {
				content = block.blockData as IHeroSliderBlockData;

				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<HeroSliderBlock 
						{...content} 
						selectedIndex={blocksManager.getHeroSlideEditing().selectedSlideIndex} 
						contentBlockRef={ref}
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							blocksManager.setHeroSlideEditing({ selectedSlideIndex: 0, selectedSlide: content.slides[0] });
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			case 'video': {
				content = block.blockData as IVideoBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
				<VideoBlock 
					{...content} 
					contentBlockRef={ref} 
					onClick={(e) => {
						e.stopPropagation();
						blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
						pageBuilderStore.setActivePanel('block-properties');
					}}
				/>);
			}

			case 'socials': {
				content = block.blockData as ISocialsBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
          <SocialsBlock
            {...content}
            contentBlockRef={ref}
            onClick={(e) => {
              e.stopPropagation();
              blocksManager.setBlockEditing(
                block.contentBlockId,
                block.blockType
              );
              pageBuilderStore.setActivePanel("block-properties");
            }}
          />
        );
			}

			case 'html': {
				content = block.blockData as IHtmlBlockData;
				
				const treeEntry = blocksManager.getDesignTree().get(block.contentBlockId);
				ref = treeEntry?.contentBlockRef as RefObject<HTMLDivElement> ?? createRef<HTMLDivElement>();

				if (!treeEntry?.contentBlockRef) {
					blocksManager.updateDesignTreeEntry(block.contentBlockId, { contentBlockRef: ref });
				}

				return (
					<HtmlBlock 
						{...content} 
						contentBlockRef={ref} 
						onClick={(e) => {
							e.stopPropagation();
							blocksManager.setBlockEditing(block.contentBlockId, block.blockType);
							pageBuilderStore.setActivePanel('block-properties');
						}}
					/>
				);
			}

			default:
				return (
					<div className="border border-red-300 text-red-500 p-2">
						Unknown block type: {block.blockType}
					</div>
				);
		}
	};

	/**
	 * Handles reordering of existing blocks within or between columns in a ColumnsBlock layout.
	 *
	 * @function handleDrop
	 * @param isFinal						Does the user want to insert the block at the end?
	 * @param blockType					The type of block being dropped (e.g. "TEXT", "IMAGE", or "DESIGN_BLOCKS")
	 * @param fromId 						The `designBlockId` of the dragged block
	 * @param toId 							The `designBlockId` of the block being hovered over (the drop target)
	 * @param fromColIndex 			The index of the column the dragged block originated from
	 * @param colContentBlockId	Is this a drop from another ColumnsBlock?
	 * @param contentBlockRef		(Optional) The DOM ref of the dragged block’s content, used for re-registration
	 * 
	 * @description
	 * This method handles rearranging blocks within the `ColumnsBlock`'s nested structure.
	 *
	 * If the dragged block came from this same column, the block is simply moved to the new position.
	 * If it came from another column, the block is removed from the source column,
	 * inserted into this column, and local references are cleaned up accordingly.
	 *
	 * @remarks
	 * - This function does NOT handle creation of new blocks. That is done via {@link drop}.
	 * - After reordering or cross-column transfer, the updated layout is serialized and refs are rebuilt.
	 */
	function handleDrop(
		isFinal: boolean, 
		blockType: string, 
		fromId: string, 
		toId: string, 
		fromColIndex: number, 
		otherColContentBlockId?: string, 
		contentBlockRef?: RefObject<HTMLElement>) {

		// This is a 2nd layer check to ensure that if the {handled: true} value hasn't fallen through, 
		// We prevent the block from being inserted/added.
		if (blockType.toLowerCase() === "columns")
		{
			setTimeout(() => blocksManager.setDropHandled(false), 200);
			return;
		}

		// (1) Dropping Content Block from SidebarPanel
		if (CONTENT_BLOCK_TYPES.has(blockType)) {
			// We are dropping a new Content Block, so let's create a new block and insert where
			// the toId DesignBlock is.
			const newDesignBlockId = crypto.randomUUID();
			const newContentBlock = createBlockContent(blockType);

			// Everything will be fresh and new.
			const newDesignBlockRef = createRef<HTMLDivElement>();
			const newContentBlockRef = createRef<HTMLElement>();
			
			newContentBlock.contentBlockRef = newContentBlockRef;
			
			// Create a new DesignBlock
			const newDesignBlock: IDesignBlock = {
				designBlockId: newDesignBlockId,
				designBlockRef: newDesignBlockRef,
				contentBlock: newContentBlock
			}

			// Next, let's get the ColumnsBlock data & information by comparing the ColumnsBlock's 
			// contentBlockId with the colContentBlockId.
			const columnsBlock = contentManager.getDesignContent().find(
				(blk: IDesignBlock) => blk.contentBlock.contentBlockId === colContentBlockId
			);
			if (!columnsBlock) return;

			// Next, we extract the blockData from the ColumnsBlock.
			const blockData = columnsBlock.contentBlock.blockData as IColumnsBlockData;

			// Then we get the list of children (an array of DesignBlock)
			const colChildren = blockData.children[colIndex];
			if (!colChildren) return;

			// Now, where did the user want to move the block to?
			const toIndex = colChildren.findIndex((blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === toId);
			
			// Register the block in the global designTree
			blocksManager.addDesignTreeEntry({
				contentBlockId: newDesignBlock.contentBlock.contentBlockId,
				contentBlockType: newDesignBlock.contentBlock.blockType.toUpperCase(),
				designBlockId: newDesignBlockId,
				designBlockRef: newDesignBlockRef,
				contentBlockRef: newContentBlockRef,
				location: "columns",
				colIndex: colIndex,
			});
			
			if (isFinal || toIndex === -1) {		
				// I'm not sure this conditional statement is necessary. Chances are, this might never be possible. 
				// But we will further assess this before removing it permanently.
				colChildren.push(newDesignBlock);
			} else {
				// Now, time to move our block into place.
				colChildren.splice(toIndex, 0, newDesignBlock);
			}
			
			// Next we sort and sync
			blocksManager.sortDesignTreeRefs();

			// We save our data/content
			contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));
			
			// Now we reset all the block management related state values.
			blocksManager.setHoveredTarget(null);
			blocksManager.setDraggingFromColIndex(null);
			blocksManager.setIsDraggingBlock(false);
			blocksManager.setHoveredDropZoneId(null);

			setTimeout(() => blocksManager.setDropHandled(false), 200);
		} else {
			// (2a) DesignCanvas -> ColumnCell. User is about to perform a cross-canvas reordering operation.
			if (fromColIndex === -1) {
				// 1. Remove from canvas (flat list)
				const canvasBlocks = contentManager.getDesignContent();
				const srcIndex = canvasBlocks.findIndex(
					(blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === fromId
				);
				if (srcIndex === -1) return;

				const [movingBlock] = canvasBlocks.splice(srcIndex, 1);

				// 2. Remove existing ref entry
				blocksManager.removeDesignTreeEntry(movingBlock.contentBlock.contentBlockId);

				// 3. Generate new identity for the column context
				const newDesignBlockId = crypto.randomUUID();
				const newDesignBlockRef = createRef<HTMLDivElement>();
				const newContentBlockRef = contentBlockRef ?? createRef<HTMLElement>();

				// 4. Insert into ColumnCell
				const columnsBlock = contentManager.getDesignContent().find(
					(blk) => blk.contentBlock.contentBlockId === colContentBlockId
				);
				if (!columnsBlock) return;

				// Final the ColumnsBlock's information and data.
				const blockData = columnsBlock.contentBlock.blockData as IColumnsBlockData;
				const colChildren = blockData.children[colIndex];
				if (!Array.isArray(colChildren)) return;
				
				// Assign it new DesignBlock.
				movingBlock.designBlockId = newDesignBlockId;
				movingBlock.designBlockRef = newDesignBlockRef;
				movingBlock.contentBlock.contentBlockRef = newContentBlockRef;
				
				// 5. Register with designTree
				blocksManager.addDesignTreeEntry({
					contentBlockId: movingBlock.contentBlock.contentBlockId,
					contentBlockType: movingBlock.contentBlock.blockType.toUpperCase(),
					designBlockId: newDesignBlockId,
					designBlockRef: newDesignBlockRef,
					contentBlockRef: newContentBlockRef,
					location: "columns",
					colIndex,
				});

				const targetIndex = colChildren.findIndex(
					(blk: IDesignBlock) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === toId
				);

				if (isFinal || targetIndex === -1) {
					// Again, not sure if this will ever be true. Waiting for further reassessment.
					// But if user is dropping onto that final DesignDropZone, we can go ahead and push into the array.
					colChildren.push(movingBlock);
				} else {
					// Let's insert our new DesignBlock into place.
					colChildren.splice(targetIndex, 0, movingBlock);
				}

				// 6. Reorder tree
				blocksManager.sortDesignTreeRefs();
				
				// Now we reset all the block management related state values.
				blocksManager.setHoveredTarget(null);
				blocksManager.setDraggingFromColIndex(null);
				blocksManager.setIsDraggingBlock(false);
				blocksManager.setHoveredDropZoneId(null);

				setTimeout(() => blocksManager.setDropHandled(false), 200);
			} else {
				// (2b) ColumnsBlock -> ColumnsBlock. Either an internal ColumnCell reordering or
				// 			ColumnsBlock to ColumnsBlock reordering.

				// 1. Figure out source and destination ColumnsBlock IDs
				const sourceColumnsBlockId = otherColContentBlockId && otherColContentBlockId !== "" ? otherColContentBlockId : colContentBlockId;
				const destColumnsBlockId = colContentBlockId;

				// 2. Locate source and destination ColumnsBlock objects
				const sourceColumnsBlock = contentManager.getDesignContent().find(
					(blk) => blk.contentBlock.contentBlockId === sourceColumnsBlockId
				);
				const destColumnsBlock = contentManager.getDesignContent().find(
					(blk) => blk.contentBlock.contentBlockId === destColumnsBlockId
				);
				if (!sourceColumnsBlock || !destColumnsBlock) return;

				const sourceBlockData = sourceColumnsBlock.contentBlock.blockData as IColumnsBlockData;
				const destBlockData = destColumnsBlock.contentBlock.blockData as IColumnsBlockData;

				// 3. Get column children arrays
				const fromChildren = sourceBlockData.children[fromColIndex];
				const toChildren = destBlockData.children[colIndex];
				if (!Array.isArray(fromChildren) || !Array.isArray(toChildren)) return;

				// 4. Log state before move (optional)
				console.log("🟡 BEFORE MOVE");
				console.log(`📦 fromCol[${sourceColumnsBlockId}][${fromColIndex}]:`);
				fromChildren.forEach((blk, i) => {
					console.log(`${i + 1}. ${blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId!} | ${blk.contentBlock.contentBlockId}`);
				});
				console.log(`📥 toCol[${destColumnsBlockId}][${colIndex}]:`);
				toChildren.forEach((blk, i) => {
					console.log(`${i + 1}. ${blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId!} | ${blk.contentBlock.contentBlockId}`);
				});

				// 5. Find indices
				const fromIndex = fromChildren.findIndex(
					(blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === fromId
				);
				const toIndex = toChildren.findIndex(
					(blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === toId
				);

				// 6. Handle same-column reorder
				if (sourceColumnsBlockId === destColumnsBlockId && fromColIndex === colIndex) {
					// 🟢 Simple reorder within same column
					if (fromIndex === -1) return;

					// Similar to how we validate it in DesignCanvas
					if (!isFinal && (toIndex === -1 || fromIndex === toIndex)) return;
					
					const [moved] = toChildren.splice(fromIndex, 1);
					
					if (isFinal) {
						// Push to the end of the destination ColumnCell.
						toChildren.push(moved);
					} else {
						if (fromIndex < toIndex)
							toChildren.splice(toIndex-1, 0, moved);
						else
							toChildren.splice(toIndex, 0, moved);
					}
					
					blocksManager.sortDesignTreeRefs();
				} else {
					// 🔁 Move across columns or ColumnsBlocks
					if (fromIndex === -1) return;

					const [movingBlock] = fromChildren.splice(fromIndex, 1);

					// Insert into destination column at correct spot
					if (isFinal || toIndex === -1) {
						// Again, not sure if this will ever be true. Waiting for further reassessment.
						// But if the user is moving a block to the end, then push it in.
						toChildren.push(movingBlock);
					} else {
						// Insert it into the right location.
						toChildren.splice(toIndex, 0, movingBlock);
					}

					// If cross-ColumnsBlock, update designTree for correct location & colIndex
					if (sourceColumnsBlockId !== destColumnsBlockId) {
						blocksManager.updateDesignTreeEntry(movingBlock.contentBlock.contentBlockId, {
							location: "columns",
							colIndex: colIndex,
							// Optionally: add destColumnsBlockId if your tree supports it
						});
					}

					console.log(`🔁 Moved block from [${sourceColumnsBlockId}][${fromColIndex}] → [${destColumnsBlockId}][${colIndex}]`);
				}
			}
		}
		
		// Now we reset all the block management related state values.
		blocksManager.setHoveredTarget(null);
		blocksManager.setDraggingFromColIndex(null);
		blocksManager.setIsDraggingBlock(false);
		blocksManager.setHoveredDropZoneId(null);

		// ✅ Final sync & save
		contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));
		blocksManager.sortDesignTreeRefs();
		
		setTimeout(() => blocksManager.setDropHandled(false), 200);
	}	

	function deleteBlock(designBlockId: string) {
		const columnsBlock = contentManager.getDesignContent().find(
			(blk) => blk.contentBlock.contentBlockId === colContentBlockId
		);
		if (!columnsBlock) return;

		const blockData = columnsBlock.contentBlock.blockData as IColumnsBlockData;

		// Traverse all columns to find and remove the block
		for (let i = 0; i < blockData.children.length; i++) {
			const col = blockData.children[i];
			const index = col.findIndex(
				(blk) => blocksManager.getDesignTree().get(blk.contentBlock.contentBlockId)?.designBlockId! === designBlockId
			);

			if (index !== -1) {
				const [removed] = col.splice(index, 1);

				// Remove from designTree
				blocksManager.removeDesignTreeEntry(removed.contentBlock.contentBlockId);

				// Re-sort the tree
				blocksManager.sortDesignTreeRefs();

				// Sync JSON
				contentManager.setJsonContent(serializationManager.fromBlocksToJSON(contentManager.getDesignContent()));

				// Optional: toast
				const name = removed.contentBlock.blockType.charAt(0).toUpperCase() + removed.contentBlock.blockType.slice(1);
				pageBuilderStore.setToast(`${name} block removed.`);

				return;
			}
		}
	}

  return (
    <div
      ref={dropRef}
      className={clsx(
				"min-h-[40px] p-2 rounded-md border border-dashed transition-all duration-200 ease-in-out",
				isInvalid
			    ? "border-red-500 bg-red-100"
			    : isOver
			      ? "border-blue-500 bg-blue-50 shadow-inner shadow-blue-100 scale-[1.01]"
			      : "border-gray-300 bg-gray-100"
			)}
			style={style}
    >
      {children.length === 0 ? (
        <span className="text-gray-400 text-sm">Drop blocks here</span>
      ) : (
        children.map((block: IDesignBlock, index: number) => {
					return (
						<Fragment key={block.designBlockId}>
							<DesignDropZone 
								isFinal={false}
								targetId={block.designBlockId}
								context={"columns"}
								onDrop={(
									isFinal: boolean,
									blockType: string,
									fromId?: string,
									otherColContentBlockId?: string,
									contentBlockId?: string,
									contentBlockRef?: RefObject<HTMLElement>
								) => { 
									const fromColIndex = blocksManager.getDraggingFromColIndex() ?? -1;
									// (1) Probably the user trying to reorder a block.
									// (2) Or, the user is trying to insert a new block at a specific position.
									handleDrop(
										isFinal,
										blockType, 
										fromId ?? "",  
										block.designBlockId, 
										fromColIndex, 
										otherColContentBlockId ?? "", 
										contentBlockRef);
								}}
							/>
							<DesignBlock
								key={block.designBlockId!}
								contentBlock={block.contentBlock}
								designBlockId={block.designBlockId!}
								designBlockRef={block.designBlockRef}
								blockContext={"columns"}
								colContentBlockId={colContentBlockId}
								colIndex={colIndex} // ✅ must pass this for cross-cell tracking
								isDragging={blocksManager.getDraggingBlockId() === block.designBlockId!}
								isEditing={currEditingBlockId === block.designBlockId!}
								isHovered={hoveredBlockId === block.designBlockId!}
								onMouseEnter={() => {
									setHoveredBlockId(block.designBlockId!);
								}}
								onMouseLeave={() => setHoveredBlockId(null)}
								onDelete={() => deleteBlock(block.designBlockId!)}
								onDragStart={(id) => {
									blocksManager.setDraggingBlock(id);
									blocksManager.setFromDesignBlockId(id);
									blocksManager.setDraggingFromColIndex(colIndex ?? -1); // -1 if from canvas
								}}
								onDragEnd={() => {
									blocksManager.setDraggingBlock(null);
								}}
								onHover={(id) => {
									if (blocksManager.getDraggingBlockId() && blocksManager.getDraggingBlockId() !== id) {
										blocksManager.setHoveredTarget(id);
									}
								}}
							>
								{renderContentBlock(block.contentBlock)}
							</DesignBlock>
						</Fragment>
					);
				})
			)}
			{children.length > 0 && (
				<DesignDropZone
					isFinal={true}
					context="canvas"
					onDrop={(
						isFinal,
						blockType: string,
						fromId?: string,
						otherColContentBlockId?: string,
						contentBlockId?: string,
						contentBlockRef?: RefObject<HTMLElement>
					) => {
						const fromColIndex = blocksManager.getDraggingFromColIndex() ?? -1;

						handleDrop(
							isFinal,
							blockType, 
							fromId ?? "",  
							"", 
							fromColIndex, 
							otherColContentBlockId ?? "", 
							contentBlockRef ?? undefined
						);
					}}
				/>
			)}
    </div>
  );
});
